import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faIdCard,
  faSun,
  faMoon,
  faCircleHalfStroke,
  faBars,
  faTimes,
  faHome,
  faTachometerAlt,
  faBell,
  faUserCog,
  faCreditCard,
  faClipboard,
  faDownload,
  faUsers,
  faKey,
  faEnvelope,
  faSms,
  faUserShield,
  faChartPie,
  faHistory,
  faFileText,
  faCalculator,
  faChartBar,
  faCrown,
  faShield,
  faShieldAlt,
  faCalendarPlus,
  faPlay,
  faShareAlt,
  faEdit,
  faPhone,
  faBook,
  faBullseye,
  faNewspaper,
  faTheaterMasks,
  faGraduationCap,
  faSignOutAlt,
  faCog,
  faLink,
  faRobot,
  faToolbox,
  faImage,
  faQrcode,
  faComments,
  faBullhorn,
  faRocket,
  faEye,
  faGlobe,
  faExclamationTriangle,
  faFileContract,
} from "@fortawesome/free-solid-svg-icons";
import NotificationBadge from "./notifications/NotificationBadge";
import { useUser } from "./context/UserContext";
import { useTheme } from "./context/ThemeContext";
import { useTranslation } from "../../i18n/LanguageContext";
import { useSubscription } from "./context/SubscriptionContext";
import { useEdition } from "./context/EditionContext";

// SidePanel menu items structure (for mobile dropdown)
const MOBILE_MENU_ITEMS = {
  SCAM_DETECT_AI_MENU: [],
  EMAIL_ANALYSIS_MENU: [
    { name: "Forwarded Emails", icon: faRobot },
    { name: "Manual Entry", icon: faEdit },
  ],
  TEXT_ANALYSIS_MENU: [
    { name: "Forwarded Texts", icon: faRobot },
    { name: "Manual Input", icon: faEdit },
    { name: "Text Conversation", icon: faComments },
  ],
  SOCIAL_PROTECTION_MENU: [
    { name: "Business/Person Check", icon: faEdit },
    { name: "Social Media Posts", icon: faShieldAlt },
  ],
  ADVANCED_TOOLS_MENU: [
    { name: "Screenshot Analysis", icon: faImage },
    { name: "Phone Number Check", icon: faPhone },
    { name: "QR Code Check", icon: faQrcode, disabled: true },
  ],

  SIMULATIONS_MENU: [
    { name: "Simulation Profile", icon: faClipboard },
    { name: "Simulation Messages", icon: faChartPie },
    { name: "Recent Simulations", icon: faHistory },
  ],
  EDUCATION_RESOURCES_MENU: [
    { name: "Training Center", icon: faGraduationCap },
    { name: "Alerts Intelligence Hub", icon: faBook },
  ],
  REPORTS_MENU: [
    { name: "Shield Reports", icon: faShieldAlt },
    { name: "Cyber-Insurance Readiness", icon: faFileContract },
    { name: "Export Data", icon: faDownload },
  ],
  PLATFORM_ADMIN_MENU: [
    { name: "Admin Dashboard", icon: faTachometerAlt },
    { name: "User Subscription Audit", icon: faCreditCard },
    { name: "Events", icon: faCalendarPlus },
    { name: "Send Email", icon: faEnvelope },
    { name: "Send SMS", icon: faSms, comingSoon: true },
    { name: "Generate Blogs", icon: faEdit },
    { name: "Manage Announcements", icon: faBullhorn },
    { name: "Create Simulation Schedule", icon: faCalendarPlus },
    { name: "Execute Simulation Schedule", icon: faPlay },
  ],
  DEMO_MENU: [
    { name: "Demo Users", icon: faUserShield },
    { name: "Event Demo Seats", icon: faCalendarPlus },
    { name: "Test Users", icon: faUserShield },
  ],
};

function HeaderAppMobile({
  isAuthenticated,
  userName,
  canSeeAccountMenu,
  canSeeGroupInfo,
  canSeeUsersMenu,
  canSeeSubscriptionMenu,
  isPlatformAdmin,
  showMobileMenu,
  mobileMenuRef,
  toggleMobileMenu,
  handleClick,
  setHeroContent,
  handleAccountClick,
  handleAccountProfileClick,
  handleSettingsClick,
  handleUsersClick,
  handleSubscriptionsClick,
  handlePaymentsClick,
  handleSignOutClick,
  theme,
  setTheme,
  unreadCount,
  canAccessFunctionalFeatures,
  getAccessDenialReason,
  isMobileScamDetectExpanded,
  setIsMobileScamDetectExpanded,
  isMobileSimulationsExpanded,
  setIsMobileSimulationsExpanded,
  isMobileEducationExpanded,
  setIsMobileEducationExpanded,
  isMobileReportsExpanded,
  setIsMobileReportsExpanded,
  isMobilePlatformAdminExpanded,
  setIsMobilePlatformAdminExpanded,
  isMobileDemoExpanded,
  setIsMobileDemoExpanded,
  selectedUser,
  selectUser,
  loggedInUser,
  hasActiveSubscription,
  handleSwitchBack,
  isReadOnlyView,
  heroContent,
  isDeactivated,
}) {
  const { isDemoSeat } = useUser();
  const { effectiveTheme } = useTheme();
  const { t, language, setLanguage } = useTranslation();
  const { canShare, isInGracePeriod, subscription } = useSubscription();
  const { isCorporate } = useEdition();

  const formatDateForBanner = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const graceEndsOn = formatDateForBanner(
    subscription?.graceEndDate || subscription?.endDate,
  );

  const cycleTheme = () => {
    const next =
      theme === "light" ? "dark" : theme === "dark" ? "auto" : "light";
    setTheme(next);
  };

  const cycleLang = () => {
    setLanguage(language.startsWith("es") ? "en" : "es");
  };

  const themeIcon =
    theme === "dark" ? faMoon : theme === "auto" ? faCircleHalfStroke : faSun;
  const themeTitle =
    theme === "dark"
      ? t("theme.dark")
      : theme === "auto"
        ? t("theme.auto")
        : t("theme.light");
  const themeLabel =
    theme === "dark"
      ? t("header.theme.dark")
      : theme === "auto"
        ? t("header.theme.auto")
        : t("header.theme.light");
  const langLabel = language.startsWith("es")
    ? t("header.lang.spanish")
    : t("header.lang.english");

  // Menu items disabled for demo seat users
  const DEMO_DISABLED_ITEMS = ["Forwarded Emails", "Forwarded Texts"];

  const shouldDisableMobileMenuItem = (itemName) => {
    return isDemoSeat && DEMO_DISABLED_ITEMS.includes(itemName);
  };

  const handleMobileMenuClick = (action) => {
    console.log("Mobile menu clicked with action:", JSON.stringify(action)); // Debug log with JSON
    console.log("Action length:", action.length); // Debug log for length
    switch (action) {
      case "Dashboard":
        setHeroContent("Dashboard");
        break;
      case "GettingStarted":
        if (canAccessFunctionalFeatures) {
          setHeroContent("Getting Started");
        }
        break;
      case "Notifications":
        setHeroContent("Notifications");
        break;
      // New Email Analysis menu items
      case "Manual Entry":
      case "Scan Email Now": // Backward compatibility
        console.log("Setting hero content to Manual Entry"); // Debug log
        setHeroContent("Manual Entry");
        break;
      case "Forwarded Emails":
      case "View Forwarded Emails": // Backward compatibility
        console.log("Setting hero content to Forwarded Emails"); // Debug log
        setHeroContent("Forwarded Emails");
        break;
      // Backward compatibility
      case "Email Analysis":
        console.log("Setting hero content to Email Analysis"); // Debug log
        setHeroContent("Email Analysis");
        break;
      case "Phone Number Check":
        console.log("Setting hero content to Phone Number Check"); // Debug log
        setHeroContent("Phone Number Check");
        break;
      // New Text/SMS Analysis menu items
      case "Manual Input":
      case "Scan Message Now": // Backward compatibility
        console.log("Setting hero content to Manual Input"); // Debug log
        setHeroContent("Manual Input");
        break;
      case "Forwarded Texts":
      case "View Forwarded Msgs": // Backward compatibility
        console.log("Setting hero content to Forwarded Texts"); // Debug log
        setHeroContent("Forwarded Texts");
        break;
      // Backward compatibility
      case "Text/SMS Analysis":
        console.log("Setting hero content to Text/SMS Analysis"); // Debug log
        setHeroContent("Text/SMS Analysis");
        break;
      // New Social Media Protection menu items
      case "Scan by URL":
        console.log("Setting hero content to Scan by URL"); // Debug log
        setHeroContent("Scan by URL");
        break;
      case "Business/Person Check":
        console.log("Setting hero content to Business/Person Check"); // Debug log
        setHeroContent("Business/Person Check");
        break;
      // Backward compatibility
      case "Social Media Analysis":
        console.log("Setting hero content to Social Media Analysis"); // Debug log
        setHeroContent("Social Media Analysis");
        break;
      default:
        console.log("Using default case for:", JSON.stringify(action)); // Debug log
        setHeroContent(action);
    }
    toggleMobileMenu();
  };

  // Helper function to collapse all other menus when one is expanded
  const collapseAllOtherMenus = (currentMenu) => {
    if (currentMenu !== "scamDetect") setIsMobileScamDetectExpanded(false);
    if (currentMenu !== "simulations") setIsMobileSimulationsExpanded(false);
    if (currentMenu !== "education") setIsMobileEducationExpanded(false);
    if (currentMenu !== "reports") setIsMobileReportsExpanded(false);
    if (currentMenu !== "platformAdmin")
      setIsMobilePlatformAdminExpanded(false);
    if (currentMenu !== "demo") setIsMobileDemoExpanded(false);
  };

  const handleMenuToggle = (menuType, currentState, setCurrentState) => {
    if (!currentState) {
      // If menu is being opened, collapse all others first
      collapseAllOtherMenus(menuType);
    }
    setCurrentState(!currentState);
  };

  // Mirror Hero.js green-border logic: only show "Viewing as" when a member's page is displayed
  const isRelevantScreen =
    heroContent === "Simulation Profile" ||
    heroContent === "Member User Info" ||
    heroContent === "Member User Info:Create" ||
    heroContent === "Member User Info:Update" ||
    heroContent === "Member User Info:Details" ||
    heroContent === "Member User Info:ResendVerification" ||
    heroContent === "Member Activity";
  const showViewingAs =
    isAuthenticated &&
    selectedUser &&
    loggedInUser &&
    selectedUser.uuid !== loggedInUser.uuid &&
    (isReadOnlyView || isRelevantScreen);

  return (
    <>
      {/* Mobile controls container */}
      <div className="mobile-controls">
        {/* Dashboard icon - only visible on mobile when authenticated and not deactivated */}
        {isAuthenticated && !isDeactivated && (
          <button
            className={`mobile-how-do-i-link${heroContent === "Dashboard" ? " nav-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setHeroContent("Dashboard");
            }}
            aria-label="Dashboard"
          >
            <FontAwesomeIcon icon={faTachometerAlt} />
            <span className="mobile-nav-btn-label">Dashboard</span>
          </button>
        )}

        {/* Getting Started icon - shown on mobile when authenticated and not deactivated */}
        {isAuthenticated && !isDeactivated && (
          <button
            className={`mobile-how-do-i-link${heroContent === "Getting Started" ? " nav-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setHeroContent("Getting Started");
            }}
            aria-label="Getting Started"
          >
            <FontAwesomeIcon icon={faRocket} />
            <span className="mobile-nav-btn-label">Get Started</span>
          </button>
        )}

        {/* Mobile Notifications Bell - hidden in view-only mode and for deactivated users */}
        {isAuthenticated &&
          !isDeactivated &&
          loggedInUser?.phoneVerified === true &&
          !isReadOnlyView && (
            <button
              className={`mobile-notifications-bell${!hasActiveSubscription ? " disabled" : ""}${heroContent === "Notifications" ? " nav-active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                if (!hasActiveSubscription) return;
                setHeroContent("Notifications");
              }}
              aria-label="Notifications"
              aria-disabled={!hasActiveSubscription}
              disabled={!hasActiveSubscription}
              title={
                !hasActiveSubscription
                  ? getAccessDenialReason ||
                    "Subscribe to a plan to enable notifications."
                  : "Notifications"
              }
            >
              <FontAwesomeIcon icon={faBell} />
              <NotificationBadge count={unreadCount} />
              <span className="mobile-nav-btn-label">Notifications</span>
            </button>
          )}

        {/* Sign Up / Log In buttons - hidden in view-only mode */}
        {!isAuthenticated && !isReadOnlyView && (
          <div className="mobile-header-auth-buttons">
            <a
              href="#signup"
              className="header-auth-link signup-link"
              onClick={handleClick}
            >
              {t("common.signUp")}
            </a>
            <a
              href="#signin"
              className="header-auth-link login-link"
              onClick={handleClick}
            >
              {t("common.logIn")}
            </a>
          </div>
        )}

        {/* Mobile menu toggle button - hidden in view-only mode */}
        {!isReadOnlyView && (
          <button
            className={`mobile-menu-toggle ${showMobileMenu ? "menu-open" : ""}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <FontAwesomeIcon icon={faBars} />
            <span className="mobile-nav-btn-label">{"\u00A0\u00A0\u00A0"}</span>
          </button>
        )}
      </div>

      {/* Viewing-as bar — in-flow below the header controls row, mobile only */}
      {showViewingAs && (
        <div className="mobile-viewing-as-bar">
          <div className="selected-user-indicator">
            <span className="viewing-as-label">{t("header.viewingAs")}</span>
            <span className="selected-user-name">
              {selectedUser.firstName} {selectedUser.lastName}
            </span>
            <button
              className="switch-back-btn"
              onClick={handleSwitchBack}
              title={t("theme.switchBack")}
            >
              {t("header.switchBack")}
            </button>
          </div>
        </div>
      )}

      {/* Mobile Menu Dropdown - hidden in view-only mode */}
      {showMobileMenu && !isReadOnlyView && (
        <div className="mobile-menu" ref={mobileMenuRef}>
          <div className="mobile-menu-header">
            <div className="mobile-theme-options">
              {/* Single cycling theme button */}
              <button
                className="mobile-theme-option active mobile-theme-option-labeled"
                onClick={cycleTheme}
                title={themeTitle}
              >
                <FontAwesomeIcon icon={themeIcon} />
                <span className="mobile-theme-option-label">{themeLabel}</span>
              </button>

              {/* Language selector */}
              <button
                className="mobile-theme-option mobile-theme-option-labeled"
                onClick={cycleLang}
                title={t("header.lang.toggleTitle")}
              >
                <FontAwesomeIcon icon={faGlobe} />
                <span className="mobile-theme-option-label">{langLabel}</span>
              </button>
            </div>

            <button
              className="mobile-menu-close"
              onClick={toggleMobileMenu}
              aria-label="Close mobile menu"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="mobile-menu-content">
            {isAuthenticated ? (
              <>
                {isInGracePeriod && (
                  <div className="mobile-grace-banner" role="status">
                    <p className="mobile-grace-banner-title">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <span>{t("ab.grace.title")}</span>
                    </p>
                    <p className="mobile-grace-banner-text">
                      {graceEndsOn
                        ? t(
                            canSeeSubscriptionMenu
                              ? "ab.grace.message.withDate"
                              : "ab.grace.nonOwner.withDate",
                          ).replace("{date}", graceEndsOn)
                        : t(
                            canSeeSubscriptionMenu
                              ? "ab.grace.message.noDate"
                              : "ab.grace.nonOwner.noDate",
                          )}
                    </p>
                    {canSeeSubscriptionMenu && (
                      <button
                        className="mobile-grace-banner-cta"
                        onClick={() => {
                          handleAccountClick();
                          toggleMobileMenu();
                        }}
                      >
                        {t("ab.grace.cta.updatePayment")}
                      </button>
                    )}
                  </div>
                )}

                {/* SECTION 2: Functional - only shown when user has access */}
                {canAccessFunctionalFeatures ? (
                  <div className="mobile-functional-section">
                    {/* Scam Detector */}
                    <div className="mobile-menu-section">
                      <button
                        className="mobile-section-header"
                        onClick={() =>
                          handleMenuToggle(
                            "scamDetect",
                            isMobileScamDetectExpanded,
                            setIsMobileScamDetectExpanded,
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faShield} />
                        &nbsp;&nbsp;{t("Scam Detector")}
                      </button>
                      {isMobileScamDetectExpanded && (
                        <div className="mobile-submenu">
                          {/* Email Analysis */}
                          <div className="mobile-submenu-group">
                            <div className="mobile-submenu-label">
                              <FontAwesomeIcon icon={faEnvelope} />
                              &nbsp;&nbsp;{t("Email Analysis")}
                            </div>
                            {MOBILE_MENU_ITEMS.EMAIL_ANALYSIS_MENU.map(
                              (item, index) => {
                                const isDisabled = shouldDisableMobileMenuItem(
                                  item.name,
                                );
                                return (
                                  <button
                                    key={index}
                                    className="mobile-submenu-item nested"
                                    onClick={() =>
                                      !isDisabled &&
                                      handleMobileMenuClick(item.name)
                                    }
                                    disabled={isDisabled}
                                    style={
                                      isDisabled
                                        ? {
                                            cursor: "not-allowed",
                                            opacity: 0.5,
                                          }
                                        : {}
                                    }
                                    title={
                                      isDisabled
                                        ? t("common.notAvailableDemo")
                                        : ""
                                    }
                                  >
                                    <FontAwesomeIcon icon={item.icon} />
                                    &nbsp;&nbsp;{t(item.name)}
                                  </button>
                                );
                              },
                            )}
                          </div>

                          {/* Text/SMS Analysis */}
                          <div className="mobile-submenu-group">
                            <div className="mobile-submenu-label">
                              <FontAwesomeIcon icon={faSms} />
                              &nbsp;&nbsp;{t("Text/SMS Analysis")}
                            </div>
                            {MOBILE_MENU_ITEMS.TEXT_ANALYSIS_MENU.map(
                              (item, index) => {
                                const isDisabled = shouldDisableMobileMenuItem(
                                  item.name,
                                );
                                return (
                                  <button
                                    key={index}
                                    className="mobile-submenu-item nested"
                                    onClick={() =>
                                      !isDisabled &&
                                      handleMobileMenuClick(item.name)
                                    }
                                    disabled={isDisabled}
                                    style={
                                      isDisabled
                                        ? {
                                            cursor: "not-allowed",
                                            opacity: 0.5,
                                          }
                                        : {}
                                    }
                                    title={
                                      isDisabled
                                        ? t("common.notAvailableDemo")
                                        : ""
                                    }
                                  >
                                    <FontAwesomeIcon icon={item.icon} />
                                    &nbsp;&nbsp;{t(item.name)}
                                  </button>
                                );
                              },
                            )}
                          </div>

                          {/* Social Media */}
                          <div className="mobile-submenu-group">
                            <div className="mobile-submenu-label">
                              <FontAwesomeIcon icon={faShareAlt} />
                              &nbsp;&nbsp;{t("Social Media")}
                            </div>
                            {MOBILE_MENU_ITEMS.SOCIAL_PROTECTION_MENU.map(
                              (item, index) => (
                                <button
                                  key={index}
                                  className="mobile-submenu-item nested"
                                  onClick={() =>
                                    handleMobileMenuClick(item.name)
                                  }
                                >
                                  <FontAwesomeIcon icon={item.icon} />
                                  &nbsp;&nbsp;{t(item.name)}
                                </button>
                              ),
                            )}
                          </div>

                          {/* More Tools */}
                          <div className="mobile-submenu-group">
                            <div className="mobile-submenu-label">
                              <FontAwesomeIcon icon={faToolbox} />
                              &nbsp;&nbsp;{t("More Tools")}
                            </div>
                            {MOBILE_MENU_ITEMS.ADVANCED_TOOLS_MENU.map(
                              (item, index) => (
                                <button
                                  key={index}
                                  className="mobile-submenu-item nested"
                                  onClick={() =>
                                    !item.disabled &&
                                    handleMobileMenuClick(item.name)
                                  }
                                  disabled={item.disabled}
                                  style={
                                    item.disabled
                                      ? { cursor: "not-allowed", opacity: 0.5 }
                                      : {}
                                  }
                                  title={
                                    item.disabled
                                      ? `${t(item.name)} (${t("common.comingSoon")})`
                                      : ""
                                  }
                                >
                                  <FontAwesomeIcon icon={item.icon} />
                                  &nbsp;&nbsp;{t(item.name)}
                                  {item.disabled && (
                                    <span
                                      style={{
                                        fontSize: "0.75rem",
                                        marginLeft: "0.5rem",
                                        opacity: 0.7,
                                      }}
                                    >
                                      ({t("common.comingSoon")})
                                    </span>
                                  )}
                                </button>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Simulations */}
                    <div className="mobile-menu-section">
                      <button
                        className="mobile-section-header"
                        onClick={() =>
                          handleMenuToggle(
                            "simulations",
                            isMobileSimulationsExpanded,
                            setIsMobileSimulationsExpanded,
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faPlay} />
                        &nbsp;&nbsp;{t("Simulations")}
                      </button>
                      {isMobileSimulationsExpanded && (
                        <div className="mobile-submenu">
                          {MOBILE_MENU_ITEMS.SIMULATIONS_MENU.filter((item) => {
                            // Simulation Messages - Platform admin only
                            if (item.name === "Simulation Messages") {
                              return isPlatformAdmin();
                            }
                            return true;
                          }).map((item, index) => (
                            <button
                              key={index}
                              className="mobile-submenu-item"
                              onClick={() => handleMobileMenuClick(item.name)}
                            >
                              <FontAwesomeIcon icon={item.icon} />
                              &nbsp;&nbsp;{t(item.name)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Education & Resources */}
                    <div className="mobile-menu-section">
                      <button
                        className="mobile-section-header"
                        onClick={() =>
                          handleMenuToggle(
                            "education",
                            isMobileEducationExpanded,
                            setIsMobileEducationExpanded,
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faGraduationCap} />
                        &nbsp;&nbsp;{t("Trainings & Alerts")}
                      </button>
                      {isMobileEducationExpanded && (
                        <div className="mobile-submenu">
                          {MOBILE_MENU_ITEMS.EDUCATION_RESOURCES_MENU.map(
                            (item, index) => (
                              <button
                                key={index}
                                className="mobile-submenu-item"
                                onClick={() => handleMobileMenuClick(item.name)}
                              >
                                <FontAwesomeIcon icon={item.icon} />
                                &nbsp;&nbsp;{t(item.name)}
                              </button>
                            ),
                          )}
                        </div>
                      )}
                    </div>

                    {/* Reports */}
                    <div className="mobile-menu-section">
                      <button
                        className="mobile-section-header"
                        onClick={() =>
                          handleMenuToggle(
                            "reports",
                            isMobileReportsExpanded,
                            setIsMobileReportsExpanded,
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faChartBar} />
                        &nbsp;&nbsp;{t("Reports")}
                      </button>
                      {isMobileReportsExpanded && (
                        <div className="mobile-submenu">
                          {MOBILE_MENU_ITEMS.REPORTS_MENU.filter((item) => {
                            // Export Data - not shown on mobile
                            if (item.name === "Export Data") {
                              return false;
                            }
                            // Cyber-Insurance Readiness - corporate account
                            // admins only. Gated purely on the account being
                            // corporate (no platform-admin override) so it
                            // never appears on an Individual/Family account.
                            if (item.name === "Cyber-Insurance Readiness") {
                              return isCorporate && canSeeAccountMenu;
                            }
                            // All other report items - available to all authenticated users
                            return true;
                          }).map((item, index) => (
                            <button
                              key={index}
                              className="mobile-submenu-item"
                              onClick={() => handleMobileMenuClick(item.name)}
                            >
                              <FontAwesomeIcon icon={item.icon} />
                              &nbsp;&nbsp;{t(item.name)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Demo - only for platform_admin role */}
                    {isPlatformAdmin() && (
                      <div className="mobile-menu-section">
                        <button
                          className="mobile-section-header"
                          onClick={() =>
                            handleMenuToggle(
                              "demo",
                              isMobileDemoExpanded,
                              setIsMobileDemoExpanded,
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faUserShield} />
                          &nbsp;&nbsp;{t("Demo")}
                        </button>
                        {isMobileDemoExpanded && (
                          <div className="mobile-submenu">
                            {MOBILE_MENU_ITEMS.DEMO_MENU.map((item, index) => (
                              <button
                                key={index}
                                className="mobile-submenu-item"
                                onClick={() => handleMobileMenuClick(item.name)}
                              >
                                <FontAwesomeIcon icon={item.icon} />
                                &nbsp;&nbsp;{t(item.name)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Platform Admin - only for platform_admin role */}
                    {isPlatformAdmin() && (
                      <div className="mobile-menu-section">
                        <button
                          className="mobile-section-header"
                          onClick={() =>
                            handleMenuToggle(
                              "platformAdmin",
                              isMobilePlatformAdminExpanded,
                              setIsMobilePlatformAdminExpanded,
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faCrown} />
                          &nbsp;&nbsp;{t("Platform Admin")}
                        </button>
                        {isMobilePlatformAdminExpanded && (
                          <div className="mobile-submenu">
                            {MOBILE_MENU_ITEMS.PLATFORM_ADMIN_MENU.map(
                              (item, index) => (
                                <button
                                  key={index}
                                  className={`mobile-submenu-item${item.comingSoon ? " disabled" : ""}`}
                                  onClick={() =>
                                    !item.comingSoon &&
                                    handleMobileMenuClick(item.name)
                                  }
                                  disabled={item.comingSoon}
                                  style={
                                    item.comingSoon
                                      ? { cursor: "not-allowed", opacity: 0.5 }
                                      : {}
                                  }
                                  title={
                                    item.comingSoon
                                      ? `${t(item.name)} (${t("common.comingSoon")})`
                                      : ""
                                  }
                                >
                                  <FontAwesomeIcon icon={item.icon} />
                                  &nbsp;&nbsp;{t(item.name)}
                                  {item.comingSoon && (
                                    <span
                                      style={{
                                        fontSize: "0.75rem",
                                        marginLeft: "0.5rem",
                                        opacity: 0.7,
                                      }}
                                    >
                                      ({t("common.comingSoon")})
                                    </span>
                                  )}
                                </button>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mobile-functional-section">
                    <div
                      className="mobile-nav-section"
                      style={{ padding: "0.75rem 1rem" }}
                    >
                      <p
                        style={{ opacity: 0.7, margin: 0, fontSize: "0.9rem" }}
                      >
                        {getAccessDenialReason || "Access restricted"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="mobile-menu-divider"></div>

                {/* SECTION 3: User Account */}
                <div className="mobile-account-section">
                  {loggedInUser?.phoneVerified === true &&
                    canSeeAccountMenu && (
                      <button
                        className="mobile-nav-item"
                        onClick={() => {
                          handleAccountClick();
                          toggleMobileMenu();
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} />
                        &nbsp;&nbsp;{t("Account & Billing")}
                      </button>
                    )}
                  {loggedInUser?.phoneVerified === true &&
                    canSeeAccountMenu &&
                    isCorporate && (
                      <button
                        className="mobile-nav-item"
                        onClick={() => {
                          handleAccountProfileClick();
                          toggleMobileMenu();
                        }}
                      >
                        <FontAwesomeIcon icon={faIdCard} />
                        &nbsp;&nbsp;{t("accountProfile.menuLabel")}
                      </button>
                    )}
                  {loggedInUser?.phoneVerified === true &&
                    !isDeactivated &&
                    canSeeUsersMenu && (
                      <button
                        className="mobile-nav-item"
                        onClick={() => {
                          handleUsersClick();
                          toggleMobileMenu();
                        }}
                      >
                        <FontAwesomeIcon icon={faUsers} />
                        &nbsp;&nbsp;
                        {canSeeAccountMenu
                          ? t("header.users")
                          : t("header.userProfile")}
                      </button>
                    )}

                  {loggedInUser?.phoneVerified === true && !isDeactivated && (
                    <button
                      className="mobile-nav-item"
                      onClick={() => {
                        handleSettingsClick();
                        toggleMobileMenu();
                      }}
                    >
                      <FontAwesomeIcon icon={faCog} />
                      &nbsp;&nbsp;{t("settings.title")}
                    </button>
                  )}
                  <button
                    className="mobile-nav-item mobile-sign-out"
                    onClick={() => {
                      handleSignOutClick();
                      toggleMobileMenu();
                    }}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    &nbsp;&nbsp;{t("common.signOut")}
                  </button>
                </div>
              </>
            ) : (
              <div className="mobile-menu-section">
                <a href="#backtomain" onClick={handleClick}>
                  <FontAwesomeIcon icon={faHome} /> {t("header.backToMain")}
                </a>
                <a
                  href="#signup"
                  className="header-auth-link signup-link"
                  onClick={handleClick}
                >
                  {t("common.signUp")}
                </a>
                <a
                  href="#signin"
                  className="header-auth-link login-link"
                  onClick={handleClick}
                >
                  {t("common.logIn")}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default HeaderAppMobile;
